import { ArrowsDownUp, Gear, X } from '@phosphor-icons/react';
import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  type Table as TableType,
  useReactTable,
} from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RRDLink } from '@/providers/RouterShim';
import type { RequestStateQuery } from '../../../@types/RequestStateQuery';
import useLocalStorage from '../../../hooks/useLocalStorage';
import Alert from '../../components/Alert';
import { Button, ButtonContent, type ButtonProps } from '../../components/Button';
import type { IFoldoutItem } from '../../components/Foldout';
import IconButtonFoldout from '../../components/IconButtonFoldout';
import IconButton from '../../components/IconButtonV2';
import InputSearch from '../../components/InputSearch';
import Loader from '../../components/Loader';
import {
  ActiveFilters,
  Table,
  TableActions,
  TableActionsButtons,
  TableActionsSearch,
  TableHeader,
  TableHeaderMain,
  TableOverflow,
  TableSelection,
  TableSelectionCancelButton,
  TableSelectionMain,
  TableWrapper,
  Tbody,
  Td,
  Th,
  Thead,
  ThSort,
  ThSortLabel,
  Tr,
} from '../../components/NativeTable';
import Pagination from '../../components/PaginationV2';
import { SrOnly } from '../../components/SrOnly';
import { StackMedium } from '../../components/Stack';
import Tabs, { type TabsTabProps } from '../../components/TabsV3';
import { Text } from '../../components/Text';
import Tooltip from '../../components/Tooltip';
import { SkeletonListPage } from '../SkeletonTemplate';
import ColumnOrderPanel from './components/ColumnOrderPanel';
import IndeterminateCheckbox from './components/IndeterminateCheckbox';
import SortPanel, { type SortPanelItem } from './components/SortPanel';

// !!!IMPORTANTE!!!
// accessorKey is required in ColumnDef if we want to be able to sort the column

export interface DataTableDataItem {
  id: unknown;
}

export interface DataTableAction<T> {
  label: string;
  icon?: React.ReactNode;
  tone?: ButtonProps['$tone'];
  requestState?: RequestStateQuery;
  resetRowSelectionOnClick?: boolean;
  onAction: (ids: T[]) => void;
}

export interface DataTableExternalFilter {
  filter: string;
  isLoading?: boolean;
  setFilter: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface DataTableDataLoad {
  isLoading?: boolean;
  loadMore?: () => void;
}
export interface DataTableManual {
  filtering?: DataTableExternalFilter;
  dataLoad?: DataTableDataLoad;
}

interface DataTableProps<T extends DataTableDataItem> {
  id: string;
  data: T[];
  columns: ColumnDef<T>[];
  actions?: DataTableAction<T>[];
  tabs?: TabsTabProps[][];
  pageSize?: number;
  defaultSort?: SortingState;
  noSearchResultsText?: string;
  manual?: DataTableManual;
  sortOrderData?: SortPanelItem[];
  /**
   * Needs to be the same as the column id
   * @example ['name', 'email']
   */
  defaultHiddenColumns?: string[];
  requestUpdateSortOrder?: RequestStateQuery;
  serverSidePagination?: {
    rowCount: number;
    filtering?: DataTableExternalFilter;
    pagination: PaginationState;
    sorting: SortingState;
    isLoading: boolean;
    setPagination: OnChangeFn<PaginationState>;
    setSorting: OnChangeFn<SortingState>;
  };
  filters?: React.ReactNode;
  infoBox?: React.ReactNode;
  customButtons?: {
    title: string;
    onClick?: (table: TableType<T>) => void;
    icon?: React.ReactNode;
    items?: IFoldoutItem[][];
    testId?: string;
  }[];
  searchInputTestId?: string;
  hideTableHeader?: boolean;
  onUpdateSortOrder?: (payload: { [key: string]: number | undefined }) => void;
  selectionText?: (count: number) => string;
  searchText?: (count: number) => string;
  getLink?: (row: T) => string | undefined;
  onRowClick?: (row: T) => void;
  highlightRows?: string[] | null;
  onHoverRow?: (id: string | null) => void;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  columnFilters?: ColumnFiltersState;
  onFilteredDataChange?: (data: T[]) => void;
  autoResetPageIndex?: boolean;
  disabledRow?: (row: T) => boolean; // Function to determine if a row should be disabled
  collapsibleSearch?: boolean;
}

const DataTable = <T extends DataTableDataItem>({
  id,
  data,
  columns: _columns,
  actions,
  tabs,
  sortOrderData,
  defaultHiddenColumns = [],
  noSearchResultsText,
  pageSize,
  defaultSort,
  manual,
  requestUpdateSortOrder,
  serverSidePagination,
  filters,
  infoBox,
  customButtons,
  searchInputTestId,
  hideTableHeader,
  onUpdateSortOrder,
  selectionText,
  searchText,
  getLink,
  onRowClick,
  highlightRows,
  onHoverRow,
  onColumnFiltersChange,
  columnFilters,
  onFilteredDataChange,
  autoResetPageIndex,
  disabledRow,
  collapsibleSearch,
}: DataTableProps<T>) => {
  const { t } = useTranslation(['common', 'other']);
  const [sorting, setSorting] = useState<SortingState>(defaultSort ?? []);
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize ?? 100,
  });
  const [isShowingSortPanel, setIsShowingSortPanel] = useState(false);
  const [isShowingColumnOrderPanel, setIsShowingColumnOrderPanel] = useState(false);

  const defaultColumnVisibility: Record<string, boolean> = {};
  for (const column of defaultHiddenColumns) {
    defaultColumnVisibility[column] = false;
  }

  const [columnVisibility, setColumnVisibility] = useLocalStorage(
    `${id}-column-visibility`,
    defaultColumnVisibility,
  );

  // Calculate the effective column visibility without modifying the state directly
  const effectiveColumnVisibility = { ...columnVisibility };
  _columns.forEach((col) => {
    if (col.meta?.forceShow) {
      effectiveColumnVisibility[col.id!] = true;
    }
  });

  const onFilter = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setGlobalFilter(e.currentTarget.value),
    [],
  );

  useEffect(() => {
    setRowSelection({});
  }, [data]);

  useEffect(() => {
    if (requestUpdateSortOrder?.isSuccess) {
      setIsShowingSortPanel(false);
    }
  }, [requestUpdateSortOrder?.isSuccess]);

  const columns: ColumnDef<T>[] =
    actions && actions.length
      ? [
          {
            id: 'select',
            enableHiding: false,
            meta: {
              headerAsString: t('selectMultiple', { ns: 'other' }),
              narrow: true,
            },
            header: ({ table }) => {
              const hasDisabledRows =
                typeof disabledRow === 'function' &&
                table.getRowModel().rows.some((r) => disabledRow?.(r.original));

              return (
                <IndeterminateCheckbox
                  {...{
                    checked: table.getIsAllPageRowsSelected(),
                    indeterminate: table.getIsSomePageRowsSelected(),
                    onChange: table.getToggleAllPageRowsSelectedHandler(),
                    onClick: (e) => e.stopPropagation(),
                    disabled: hasDisabledRows,
                  }}
                />
              );
            },
            cell: ({ row }) => (
              <IndeterminateCheckbox
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect() || disabledRow?.(row.original),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                  onClick: (e) => e.stopPropagation(),
                }}
              />
            ),
          },
          ..._columns,
        ]
      : _columns;

  const initialColumnOrder = columns
    .map((column) => column.id)
    .filter((id): id is string => !!id && id !== 'select');

  const [columnOrderStored, setColumnOrder] = useLocalStorage<string[]>(
    `${id}-columns-order`,
    initialColumnOrder,
  );

  const columnOrder = useMemo(() => {
    const order = [...columnOrderStored];
    if (actions?.length) {
      order.unshift('select');
    }
    return order;
  }, [actions?.length, columnOrderStored]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: serverSidePagination ? serverSidePagination.setSorting : setSorting,
    onPaginationChange: serverSidePagination ? serverSidePagination.setPagination : setPagination,
    autoResetPageIndex,
    onColumnOrderChange: setColumnOrder,
    manualFiltering: !!manual?.filtering || !!serverSidePagination,
    manualPagination: !!manual?.dataLoad || !!serverSidePagination,
    manualSorting: !!serverSidePagination,
    rowCount: serverSidePagination?.rowCount,
    state: {
      sorting: serverSidePagination?.sorting ?? sorting,
      globalFilter,
      columnVisibility: effectiveColumnVisibility,
      rowSelection,
      pagination: serverSidePagination?.pagination ?? pagination,
      columnOrder,
      columnFilters,
    },
  });

  const selectedRows = table.getSelectedRowModel().flatRows.map(({ original }) => original);

  useEffect(() => {
    // Reset row selection when action is completed
    setRowSelection({});
  }, [actions?.map(({ requestState }) => requestState?.isSuccess).join('')]);

  const filteredRowsRef = useRef<T[]>([]);

  useEffect(() => {
    if (!onFilteredDataChange) {
      return;
    }

    const filteredData = table.getFilteredRowModel().rows.map((row) => row.original);
    const currentFilteredDataString = JSON.stringify(filteredData);
    const previousFilteredDataString = JSON.stringify(filteredRowsRef.current);

    if (currentFilteredDataString !== previousFilteredDataString) {
      filteredRowsRef.current = filteredData;
      onFilteredDataChange(filteredData);
    }
  }, [table.getFilteredRowModel().rows, onFilteredDataChange]);

  const filter =
    manual?.filtering?.filter ?? serverSidePagination?.filtering?.filter ?? globalFilter ?? '';
  const filterCallback =
    manual?.filtering?.setFilter ?? serverSidePagination?.filtering?.setFilter ?? onFilter;

  const isServerSideLoading = serverSidePagination?.isLoading ?? false;
  const rows = table.getRowModel().rows;

  const isCurrentlyDefaultOrder = columnOrder.join(',') === initialColumnOrder.join(',');
  const isCurrentlyDefaultVisibility = columns.every(
    ({ id }) =>
      !id ||
      (defaultHiddenColumns.includes(id)
        ? columnVisibility[id] === false
        : columnVisibility[id] !== false),
  );

  return (
    <>
      {sortOrderData && onUpdateSortOrder && isShowingSortPanel && (
        <SortPanel
          isLoading={requestUpdateSortOrder?.isLoading ?? false}
          items={sortOrderData}
          onClose={() => setIsShowingSortPanel(false)}
          updateSortOrder={onUpdateSortOrder}
        />
      )}

      {isShowingColumnOrderPanel && (
        <ColumnOrderPanel
          columns={table.getAllLeafColumns().filter((x) => x.id !== 'select')}
          onClose={() => setIsShowingColumnOrderPanel(false)}
          onOrderChange={(columns) => {
            const columnOrder = columns.map((column) => column.id);
            setColumnOrder(columnOrder);
          }}
          onResetToDefault={() => {
            setColumnOrder(initialColumnOrder);
            setColumnVisibility(defaultColumnVisibility);
          }}
          isCurrentlyDefault={isCurrentlyDefaultOrder && isCurrentlyDefaultVisibility}
        />
      )}

      <StackMedium>
        <TableWrapper>
          {!hideTableHeader && (
            <TableHeader>
              <TableHeaderMain>
                {tabs && tabs.length > 0 ? <Tabs tabs={tabs} /> : <div />}
                <TableActions>
                  <TableActionsSearch>
                    <InputSearch
                      placeholder={
                        (searchText && searchText(rows.length)) ?? t('search', { ns: 'common' })
                      }
                      label={t('search', { ns: 'common' })}
                      id={`${id}-search`}
                      value={filter}
                      onChange={filterCallback}
                      loading={manual?.filtering?.isLoading}
                      sizeIconButton
                      collapsible={collapsibleSearch}
                      testId={searchInputTestId}
                    />
                  </TableActionsSearch>
                  <TableActionsButtons>
                    {customButtons &&
                      customButtons.map(({ title, icon, items, onClick, testId }) => (
                        <Tooltip key={title} tooltip={title}>
                          {onClick && (
                            <IconButton
                              onClick={() => onClick(table)}
                              title={title}
                              data-testid={testId}
                            >
                              {icon}
                            </IconButton>
                          )}
                          {items && <IconButtonFoldout items={items} title={title} disableTab />}
                        </Tooltip>
                      ))}
                    {sortOrderData && (
                      <Tooltip tooltip={t('sortOrder', { ns: 'other' })}>
                        <IconButton
                          onClick={() => setIsShowingSortPanel(true)}
                          title={t('sortOrder', { ns: 'other' })}
                        >
                          <ArrowsDownUp size={20} weight="bold" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip tooltip={t('customize', { ns: 'common' })}>
                      <IconButton
                        onClick={() => setIsShowingColumnOrderPanel(true)}
                        title={t('customize', { ns: 'common' })}
                      >
                        <Gear size={20} weight="bold" />
                      </IconButton>
                    </Tooltip>
                  </TableActionsButtons>
                </TableActions>
              </TableHeaderMain>
              {filters && <ActiveFilters>{filters}</ActiveFilters>}
            </TableHeader>
          )}
          {infoBox && <div style={{ marginBottom: '24px' }}>{infoBox}</div>}
          {selectedRows.length > 0 && (
            <TableSelection>
              <TableSelectionMain>
                <TableSelectionCancelButton
                  onClick={() => table.resetRowSelection()}
                  title={t('clear', { ns: 'common' })}
                >
                  <X size={16} weight="bold" />
                  <SrOnly>{t('clearChoices', { ns: 'common' })}</SrOnly>
                </TableSelectionCancelButton>
                <Text $weight="bold" $size="xsmall">
                  {selectionText
                    ? selectionText(selectedRows.length)
                    : t('numberOfSelected', {
                        ns: 'other',
                        selected: selectedRows.length,
                        count: selectedRows.length,
                      })}
                </Text>
              </TableSelectionMain>
              {actions &&
                actions?.map(
                  ({ label, icon, resetRowSelectionOnClick, tone = 'errorClear', onAction }) => (
                    <Button
                      key={label}
                      $size="tiny"
                      $tone={tone}
                      onClick={() => {
                        onAction(selectedRows);
                        if (resetRowSelectionOnClick) {
                          setRowSelection({});
                        }
                      }}
                    >
                      {icon}
                      {label}
                    </Button>
                  ),
                )}
            </TableSelection>
          )}
          {isServerSideLoading && (
            <StackMedium>
              <SkeletonListPage />
            </StackMedium>
          )}
          {!isServerSideLoading && rows.length === 0 && (
            <Alert tone="warning" message={noSearchResultsText} />
          )}
          {!isServerSideLoading && rows.length > 0 && (
            <TableOverflow>
              <Table>
                <Thead>
                  {table.getHeaderGroups().map(({ id, headers }) => (
                    <Tr key={id}>
                      {headers.map(({ id, isPlaceholder, column, getContext }) => (
                        <Th key={id} $isMinimal={column.columnDef.meta?.narrow}>
                          {isPlaceholder ? null : (
                            <>
                              {column.getCanSort() ? (
                                <ThSort
                                  onClick={column.getToggleSortingHandler()}
                                  title={
                                    column.getNextSortingOrder() === 'asc'
                                      ? t('sortAscending', { ns: 'common' })
                                      : column.getNextSortingOrder() === 'desc'
                                        ? t('sortDescending', {
                                            ns: 'common',
                                          })
                                        : t('clearSort', { ns: 'common' })
                                  }
                                >
                                  {flexRender(column.columnDef.header, getContext())}
                                  {{
                                    asc: (
                                      <svg
                                        width="8"
                                        height="12"
                                        viewBox="0 0 8 12"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <title>
                                          {t('sortAscending', {
                                            ns: 'common',
                                          })}
                                        </title>
                                        <path d="M4 12L0 8L8 8L4 12Z" />
                                      </svg>
                                    ),
                                    desc: (
                                      <svg
                                        width="8"
                                        height="12"
                                        viewBox="0 0 8 12"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <title>
                                          {t('sortDescending', {
                                            ns: 'common',
                                          })}
                                        </title>
                                        <path d="M4 -7.93662e-09L8 4L5.82138e-07 4L4 -7.93662e-09Z" />
                                      </svg>
                                    ),
                                  }[column.getIsSorted() as string] ?? (
                                    <svg
                                      width="8"
                                      height="12"
                                      viewBox="0 0 8 12"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <title>{t('clearSort', { ns: 'common' })}</title>
                                      <path d="M4 -7.93662e-09L8 4L5.82138e-07 4L4 -7.93662e-09Z" />
                                      <path d="M4 12L0 8L8 8L4 12Z" />
                                    </svg>
                                  )}
                                </ThSort>
                              ) : (
                                <ThSortLabel>
                                  {flexRender(column.columnDef.header, getContext())}
                                </ThSortLabel>
                              )}
                            </>
                          )}
                        </Th>
                      ))}
                    </Tr>
                  ))}
                </Thead>
                <Tbody>
                  {rows.map(
                    ({
                      id,
                      original,
                      getVisibleCells,
                      getToggleSelectedHandler,
                      getIsSelected,
                    }) => {
                      if (onRowClick) {
                        return (
                          <Tr
                            key={id}
                            onClick={() => onRowClick(original)}
                            title={t('goTo', { ns: 'common' })}
                            tabIndex={0}
                            $isClickable
                            onMouseEnter={() => onHoverRow?.(original.id as string)}
                            onMouseLeave={() => onHoverRow?.(null)}
                            $highlight={highlightRows?.includes(original.id as string)}
                          >
                            {getVisibleCells().map(({ id, column, getContext }) => (
                              <Td
                                key={id}
                                data-heading={
                                  typeof column.columnDef.header === 'string'
                                    ? column.columnDef.header
                                    : ''
                                }
                                onClick={(e) => {
                                  // Prevent clicking on the checkbox td from navigating to link
                                  if (column.id === 'select') {
                                    e.stopPropagation();
                                    e.preventDefault();

                                    if (!disabledRow?.(original)) {
                                      getToggleSelectedHandler()({
                                        target: {
                                          checked: !getIsSelected(),
                                        },
                                      });
                                    }
                                  }
                                }}
                              >
                                {flexRender(column.columnDef.cell, getContext())}
                              </Td>
                            ))}
                          </Tr>
                        );
                      }
                      const linkHref = getLink && getLink(original);

                      return (
                        <Tr
                          key={id}
                          to={linkHref ?? ''}
                          as={linkHref ? RRDLink : undefined}
                          title={linkHref ? t('goTo', { ns: 'common' }) : undefined}
                          tabIndex={linkHref ? 0 : undefined}
                          $isClickable={!!linkHref}
                          onMouseEnter={() => onHoverRow?.(original.id as string)}
                          onMouseLeave={() => onHoverRow?.(null)}
                          $highlight={highlightRows?.includes(original.id as string)}
                        >
                          {getVisibleCells().map(({ id, column, getContext }) => (
                            <Td
                              key={id}
                              data-heading={
                                typeof column.columnDef.header === 'string'
                                  ? column.columnDef.header
                                  : ''
                              }
                              onClick={(e) => {
                                // Prevent clicking on the checkbox td from navigating to link
                                if (column.id === 'select') {
                                  e.stopPropagation();
                                  e.preventDefault();

                                  if (!disabledRow?.(original)) {
                                    getToggleSelectedHandler()({
                                      target: {
                                        checked: !getIsSelected(),
                                      },
                                    });
                                  }
                                }
                              }}
                            >
                              {flexRender(column.columnDef.cell, getContext())}
                            </Td>
                          ))}
                        </Tr>
                      );
                    },
                  )}
                </Tbody>
              </Table>
            </TableOverflow>
          )}
        </TableWrapper>
      </StackMedium>
      {manual?.dataLoad?.loadMore && (
        <StackMedium>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button $tone="primary" onClick={manual.dataLoad.loadMore}>
              <ButtonContent>{t('loadMore', { ns: 'common' })}</ButtonContent>
              {manual.dataLoad.isLoading && (
                <ButtonContent>
                  <Loader inline />
                </ButtonContent>
              )}
            </Button>
          </div>
        </StackMedium>
      )}
      {(!manual?.dataLoad || !!serverSidePagination) && table.getPageCount() > 1 && (
        <StackMedium>
          <Pagination
            currentPage={table.getState().pagination.pageIndex + 1}
            prev={{
              onClick: () => table.previousPage(),
              disabled: !table.getCanPreviousPage(),
              title: t('goToPreviousPage', { ns: 'common' }),
            }}
            next={{
              onClick: () => table.nextPage(),
              disabled: !table.getCanNextPage(),
              title: t('goToNextPage', { ns: 'common' }),
            }}
            pages={{
              value: table.getState().pagination.pageIndex + 1,
              onChange: (e) => table.setPageIndex(Number(e.target.value) - 1),
              pages: Array.from({ length: table.getPageCount() }, (_, i) => ({
                value: i + 1,
              })),
            }}
          />
        </StackMedium>
      )}
    </>
  );
};

export default DataTable;
